import { Capacitor } from '@capacitor/core'
import { db } from '../../database/schema.js'
import { logger } from '../../utils/logger.js'
import type {
  EmergencyDataType,
  EmergencyGeohashFile,
  EmergencyIndex,
  AnyEmergencyFacility,
} from './emergency-types.js'

const log = logger.create('emergency')

const DATA_ORIGIN = Capacitor.isNativePlatform() ? 'https://taselith.com' : ''
const BASE_URL = `${DATA_ORIGIN}/data/emergency`

const BASE32 = '0123456789bcdefghjkmnpqrstuvwxyz'

const GEOHASH_NEIGHBORS: Record<string, Record<string, string>> = {
  right: { even: 'bc01fg45238967deuvhjyznpkmstqrwx', odd: 'p0r21436x8zb9dcf5h7kjnmqesgutwvy' },
  left: { even: '238967debc01fg45uvhjyznpkmstqrwx', odd: '14365h7k9dcfesgujnmqp0r2twvyx8zb' },
  top: { even: 'p0r21436x8zb9dcf5h7kjnmqesgutwvy', odd: 'bc01fg45238967deuvhjyznpkmstqrwx' },
  bottom: { even: '14365h7k9dcfesgujnmqp0r2twvyx8zb', odd: '238967debc01fg45uvhjyznpkmstqrwx' },
}

const GEOHASH_BORDERS: Record<string, Record<string, string>> = {
  right: { even: 'bcfguvyz', odd: 'prxz' },
  left: { even: '0145hjnp', odd: '028b' },
  top: { even: 'prxz', odd: 'bcfguvyz' },
  bottom: { even: '028b', odd: '0145hjnp' },
}

function neighborGeohash(hash: string, direction: string): string {
  const lastChar = hash.slice(-1)
  let parent = hash.slice(0, -1)
  const type = hash.length % 2 === 0 ? 'odd' : 'even'

  if (GEOHASH_BORDERS[direction][type].includes(lastChar) && parent.length > 0) {
    parent = neighborGeohash(parent, direction)
  }

  return parent + BASE32[GEOHASH_NEIGHBORS[direction][type].indexOf(lastChar)]
}

function encodeGeohash(lat: number, lon: number, precision: number): string {
  let minLat = -90,
    maxLat = 90,
    minLon = -180,
    maxLon = 180
  let hash = ''
  let isLon = true
  let bit = 0
  let ch = 0

  while (hash.length < precision) {
    if (isLon) {
      const mid = (minLon + maxLon) / 2
      if (lon >= mid) {
        ch = ch | (1 << (4 - bit))
        minLon = mid
      } else {
        maxLon = mid
      }
    } else {
      const mid = (minLat + maxLat) / 2
      if (lat >= mid) {
        ch = ch | (1 << (4 - bit))
        minLat = mid
      } else {
        maxLat = mid
      }
    }
    isLon = !isLon
    if (bit < 4) {
      bit++
    } else {
      hash += BASE32[ch]
      bit = 0
      ch = 0
    }
  }
  return hash
}

function coveringGeohashes(
  south: number,
  west: number,
  north: number,
  east: number,
  precision: number
): string[] {
  const set = new Set<string>()
  const step = precision === 3 ? 1.5 : 0.3
  for (let lat = south; lat <= north; lat += step) {
    for (let lon = west; lon <= east; lon += step) {
      const gh = encodeGeohash(lat, lon, precision)
      set.add(gh)
      set.add(neighborGeohash(gh, 'right'))
      set.add(neighborGeohash(gh, 'top'))
      set.add(neighborGeohash(gh, 'left'))
      set.add(neighborGeohash(gh, 'bottom'))
    }
  }
  const ne = encodeGeohash(north, east, precision)
  set.add(ne)
  return [...set]
}

function haversineKm(lat1: number, lon1: number, lat2: number, lon2: number): number {
  const R = 6371
  const dLat = ((lat2 - lat1) * Math.PI) / 180
  const dLon = ((lon2 - lon1) * Math.PI) / 180
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos((lat1 * Math.PI) / 180) * Math.cos((lat2 * Math.PI) / 180) * Math.sin(dLon / 2) ** 2
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
}

class EmergencyDataService {
  private indexCache: EmergencyIndex | null = null

  async getIndex(): Promise<EmergencyIndex | null> {
    if (this.indexCache) return this.indexCache
    try {
      const resp = await fetch(`${BASE_URL}/index.json`)
      if (!resp.ok) return null
      this.indexCache = await resp.json()
      return this.indexCache
    } catch (err) {
      log.warn('Failed to fetch emergency index', err)
      return null
    }
  }

  async getGeohashData(
    dataType: EmergencyDataType,
    geohash: string
  ): Promise<EmergencyGeohashFile | null> {
    const cacheKey = `${dataType}/${geohash}`
    try {
      const cached = await db.emergencyCache.get(cacheKey)
      if (cached) {
        this.fetchAndCache(dataType, geohash, cacheKey).catch(() => {})
        return cached.data
      }
    } catch {
      // IndexedDB not available or table missing — continue with network
    }

    return this.fetchAndCache(dataType, geohash, cacheKey)
  }

  private async fetchAndCache(
    dataType: EmergencyDataType,
    geohash: string,
    cacheKey: string
  ): Promise<EmergencyGeohashFile | null> {
    try {
      const resp = await fetch(`${BASE_URL}/${dataType}/${geohash}.json`)
      if (!resp.ok) {
        if (resp.status === 404) return null
        return null
      }
      const data: EmergencyGeohashFile = await resp.json()
      try {
        await db.emergencyCache.put({
          key: cacheKey,
          dataType,
          geohash,
          data,
          fetchedAt: new Date().toISOString(),
        })
      } catch {
        // IndexedDB write failed — proceed without caching
      }
      return data
    } catch (err) {
      log.warn(`Failed to fetch ${cacheKey}`, err)
      return null
    }
  }

  async queryByBounds(
    dataType: EmergencyDataType,
    south: number,
    west: number,
    north: number,
    east: number
  ): Promise<AnyEmergencyFacility[]> {
    const geohashes = coveringGeohashes(south, west, north, east, 3)
    const index = await this.getIndex()
    const validGeohashes = index
      ? geohashes.filter(gh => index.datasets[dataType].geohashes.includes(gh))
      : geohashes

    const results: AnyEmergencyFacility[] = []
    const fetches = validGeohashes.map(async gh => {
      const data = await this.getGeohashData(dataType, gh)
      if (data) {
        for (const f of data.features) {
          if (
            f.properties.lat >= south &&
            f.properties.lat <= north &&
            f.properties.lon >= west &&
            f.properties.lon <= east
          ) {
            results.push(f.properties)
          }
        }
      }
    })
    await Promise.all(fetches)
    return results
  }

  async queryNearest(
    dataType: EmergencyDataType,
    lat: number,
    lon: number,
    count: number = 3,
    radiusKm: number = 200
  ): Promise<Array<AnyEmergencyFacility & { distanceKm: number }>> {
    const degOffset = radiusKm / 111
    const south = lat - degOffset
    const north = lat + degOffset
    const west = lon - degOffset / Math.cos((lat * Math.PI) / 180)
    const east = lon + degOffset / Math.cos((lat * Math.PI) / 180)

    const all = await this.queryByBounds(dataType, south, west, north, east)
    return all
      .map(f => ({ ...f, distanceKm: haversineKm(lat, lon, f.lat, f.lon) }))
      .sort((a, b) => a.distanceKm - b.distanceKm)
      .slice(0, count)
  }

  async queryNearestFiltered<T extends AnyEmergencyFacility>(
    dataType: EmergencyDataType,
    lat: number,
    lon: number,
    filter: (f: AnyEmergencyFacility) => f is T,
    count: number = 3,
    radiusKm: number = 200
  ): Promise<Array<T & { distanceKm: number }>> {
    const degOffset = radiusKm / 111
    const south = lat - degOffset
    const north = lat + degOffset
    const west = lon - degOffset / Math.cos((lat * Math.PI) / 180)
    const east = lon + degOffset / Math.cos((lat * Math.PI) / 180)

    const all = await this.queryByBounds(dataType, south, west, north, east)
    return all
      .filter(filter)
      .map(f => ({ ...f, distanceKm: haversineKm(lat, lon, f.lat, f.lon) }))
      .sort((a, b) => a.distanceKm - b.distanceKm)
      .slice(0, count)
  }
}

export const emergencyDataService = new EmergencyDataService()
